<template>
  <Container>
    <PageHeader :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <h4 class="card-title mb-0">Discounts</h4>
              <b-button v-b-modal.discount-modal @click="clearDiscount" variant="outline-primary">
                Add Discount
              </b-button>
            </div>

            <b-modal size="lg" id="discount-modal" title="Create Discount" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="discount-form" @submit.prevent="handleSubmit(onSubmit)">
                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Title" label-for="discount_title">
                        <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                          <b-form-input
                            v-model="discount.title"
                            id="discount_title"
                            placeholder="Enter title"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Code" label-for="discount_code">
                        <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                          <b-form-input
                            v-model="discount.code"
                            id="discount_code"
                            placeholder="Enter promo code"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Value" label-for="value">
                        <b-input-group class="w-100">
                          <ValidationProvider rules="required" v-slot="{ errors }">
                            <b-form-input
                              id="value"
                              v-model="discount.value"
                              type="number"
                              step=".01"
                              placeholder="Enter value"
                            ></b-form-input>

                            <small class="text-danger">{{ errors[0] }}</small>
                          </ValidationProvider>

                          <template #append>
                            <b-form-select
                              v-model="discount.percentage"
                              :options="[
                                { value: false, text: '$' },
                                { value: true, text: '%' },
                              ]"
                            >
                            </b-form-select>
                          </template>
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Expiry date" label-for="expiry_date">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="discount.expiry_date"
                            class="w-100"
                            id="expiry_date"
                            placeholder="Expiry date"
                            :disabled-date="disabledBeforeTomorrow"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group label="Lodgings" label-for="discount_lodgings">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="discount_lodgings"
                        v-model="lodging_ids"
                        :options="lodgingsName"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                        @select="addRemovedPriceCut"
                        @remove="removePriceCut"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('discount-modal')">Close</b-button>
                <b-button form="discount-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length mb-3">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select class="mx-1" v-model="perPage" size="sm" :options="pageOptions"></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>

              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <b-table
                :items="discountItems"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(actions)="row">
                  <b-dropdown variant="default" dropleft>
                    <template v-slot:button-content>
                      <i class="fas fa-bars"></i>
                      <i class="bx bx-caret-down"></i>
                    </template>

                    <b-dropdown-item v-b-modal.discount-modal @click="toggleEdit(row.item.id)">
                      <i class="fa fa-pencil-alt"></i>
                      Edit
                    </b-dropdown-item>

                    <b-dropdown-item :to="{ path: 'discounts/' + row.item.id }">
                      <i class="fa fa-info-circle"></i>
                      Details
                    </b-dropdown-item>

                    <b-dropdown-item link-class="text-danger">
                      <i class="fas fa-trash"></i>
                      Delete
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script>
import Container from "../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { getLodgings, createDiscount, getDiscounts, updateDiscount } from "@/store/api";
import moment from "moment";

export default {
  page: {
    title: "Discounts",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Container,
    PageHeader,
  },

  data() {
    return {
      discount: {
        title: "",
        code: "",
        value: "",
        percentage: false,
        expiry_date: "",
        price_cuts_attributes: [],
      },
      discounts: [],
      lodging_ids: [],
      lodgings: [],
      update: true,
      discountId: "",
      items: [
        {
          text: "Home",
          to: "/",
        },
        {
          text: "Settings",
          to: "/settings",
        },
        {
          text: "Discounts",
          active: true,
        },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "",
      sortDesc: false,
      fields: [
        { key: "id" },
        { key: "title" },
        { key: "code" },
        { key: "value" },
        { key: "expiry_date" },
        { key: "actions", tdClass: "text-right" },
      ],
    };
  },

  created() {
    getLodgings().then((response) => (this.lodgings = response));
    getDiscounts().then((response) => (this.discounts = response));
  },

  mounted() {
    this.totalRows = this.items.length;
  },

  computed: {
    rows() {
      return this.lodgingItems.length;
    },

    lodgingsName() {
      return this.lodgings.map((lodging) => ({ value: lodging.id, text: lodging.attributes.name }));
    },

    discountItems() {
      return this.discounts.map((discount) => {
        return {
          id: discount.id,
          title: discount.attributes.title,
          code: discount.attributes.code,
          value: `${discount.attributes.value}${discount.attributes.percentage ? "%" : "$"}`,
          expiry_date: moment(discount.attributes.expiry_date).format("MMM DD, YYYY"),
        };
      });
    },
  },

  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    removePriceCut(lodging) {
      this.discount.price_cuts_attributes.push({ id: lodging.id, lodging_id: lodging.value, _destroy: true });
    },

    addRemovedPriceCut(lodging) {
      let priceCuts = this.discount.price_cuts_attributes.filter((priceCut) => priceCut.lodging_id == lodging.value);
      if (priceCuts.length != 0) {
        lodging.id = priceCuts[0].id;
        this.discount.price_cuts_attributes = this.discount.price_cuts_attributes.filter(
          (priceCut) => priceCut.lodging_id != lodging.value
        );
      }
    },

    disabledBeforeTomorrow(date) {
      return date < moment().subtract(1, "days");
    },

    clearDiscount() {
      this.update = false;
      this.discount.title = "";
      this.discount.code = "";
      this.discount.value = "";
      this.discount.percentage = false;
      this.discount.expiry_date = "";
      this.discount.price_cuts_attributes = [];
      this.lodging_ids = "";
    },

    toggleEdit(id) {
      this.clearDiscount();
      this.update = true;
      this.discountId = id;
      let selectedDiscount = this.discounts.find((discount) => discount.id == id);
      this.discount.title = selectedDiscount.attributes.title;
      this.discount.code = selectedDiscount.attributes.code;
      this.discount.value = selectedDiscount.attributes.value;
      this.discount.percentage = selectedDiscount.attributes.percentage;
      this.discount.expiry_date = new Date(selectedDiscount.attributes.expiry_date);
      this.lodging_ids = selectedDiscount.attributes.price_cuts.data.map((priceCut) => {
        return {
          text: priceCut.attributes.lodging.name,
          value: priceCut.attributes.lodging.id,
          id: priceCut.id,
          _destroy: false,
        };
      });
    },

    onSubmit() {
      this.$bvModal.hide("discount-modal");
      let priceCuts = this.lodging_ids.map((lodging_id) => {
        return { id: lodging_id.id, lodging_id: lodging_id.value };
      });
      this.discount.price_cuts_attributes = this.discount.price_cuts_attributes.concat(priceCuts);
      if (this.update) {
        updateDiscount(this.discount, this.discountId).then((response) => {
          let index = this.discounts.findIndex((discount) => discount.id === this.discountId);
          this.discounts.splice(index, 1);
          this.discounts.splice(index, 0, response);
        });
      } else {
        createDiscount(this.discount).then((response) => this.discounts.push(response));
      }
    },
  },
};
</script>

<style scoped>
.icon-add {
  font-size: 35px;
}
</style>
